<script lang="ts">
// eslint-disable-next-line @typescript-eslint/naming-convention
const Operator = {
  '=': 'equals',
  '≤': 'lessThanOrEqual',
  '≥': 'greaterThanOrEqual',
  '<': 'lessThan',
  '>': 'greaterThan',
} as const;
type Operator = (typeof Operator)[keyof typeof Operator];

interface FilterModel {
  filter: string;
  type: Operator;
}

export interface LocalFilterModel {
  filter: string;
  type: Operator;
}

const identity = <T,>(value: T) => value;
</script>

<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeMount, ref, shallowRef, watch } from 'vue';
import { IFloatingFilterParams } from 'ag-grid-enterprise';
import Wrapper from './FilterWrapper.vue';
import InputFormattedNumber from './InputFormattedNumber.vue';

const localFilterModel = ref<LocalFilterModel>({ filter: '', type: Operator['='] });

const filterInput = computed({
  get() {
    if (localFilterModel.value.filter === '') return '';
    const valueConverter = params.value?.filterParams.valueConverter?.get ?? identity;
    return valueConverter(localFilterModel.value.filter);
  },
  set(value: string) {
    const valueConverter = params.value?.filterParams.valueConverter?.set ?? identity;
    localFilterModel.value.filter = value === '' ? '' : valueConverter(value);
  },
});

const params = shallowRef<
  IFloatingFilterParams & {
    filterParams: {
      allowNegative?: boolean;
      valueConverter?: { get(value: string): string; set(value: string): string };
    };
  }
>();

defineExpose({
  onParentModelChanged: updateLocalFilter,
});

onBeforeMount(() => {
  params.value = getCurrentInstance()?.proxy.$data.params;
  if (params.value === undefined) return;

  const filterModel = params.value.api.getColumnFilterModel<FilterModel>(params.value.column);
  updateLocalFilter(filterModel);
});

function updateLocalFilter(filterModel: FilterModel | null) {
  if (filterModel === null) {
    localFilterModel.value.type = Operator['='];
    localFilterModel.value.filter = '';
  } else {
    localFilterModel.value = filterModel;
  }
}

watch(localFilterModel, applyFilter, { deep: true, immediate: true });

async function applyFilter() {
  if (params.value === undefined) return;
  await params.value.api.setColumnFilterModel<FilterModel>(
    params.value.column,
    localFilterModel.value.filter === '' ? null : localFilterModel.value
  );
  params.value.api.onFilterChanged();
}
</script>

<template>
  <Wrapper :data-test-filter="params?.column.getColId()">
    <select
      v-model="localFilterModel.type"
      class="ag-input-field-input ag-text-field-input"
      style="width: 20px; text-align: center"
    >
      <option v-for="(value, text) in Operator" :key="text" :value="value">{{ text }}</option>
    </select>
    <InputFormattedNumber
      v-model="filterInput"
      allow-decimal
      :allow-negative="params?.filterParams.allowNegative ?? true"
      autocomplete="off"
      class="ag-input-field-input ag-text-field-input"
    />
  </Wrapper>
</template>
